import {AfterViewInit, Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {GenderPerAgeChartData, PieChartData} from '../../../../models/chart';
import {ReferenceDataService} from '../../../services/reference-data/reference-data.service';
import * as Sentry from '@sentry/browser';
import {PdfExportService} from '../../../services/export-pdf/pdf-export.service';
import {UserService} from '../../../services/user.service';
import {TranslateService} from '@ngx-translate/core';
import { AppSettings } from '../../../utils/app-settings';
import { CommonConstant } from '../../../app.constants';

export enum InfluencerDemographicGender {
  Female = 'Female',
  Male = 'Male'
}
@Component({
  selector: 'influencer-demographics',
  templateUrl: './influencer-demographics.component.html',
  styleUrls: ['./influencer-demographics.component.scss']
})
export class InfluencerDemographicsComponent implements OnInit, AfterViewInit {
  static COMPONENT_NAME = 'InfluencerDemographicsComponent';
  private rawChartData: { content_persons_statistic: any; content_geo_statistic: any; audience_interests: any; audience_languages: any; brand_affinity: any; audience_brand_affinity: any; };
  public genderChartData: Array<PieChartData>;
  public countryChartData: Array<PieChartData>;
  public audienceInterestChartData: Array<PieChartData>;
  public nativeLanguageChartData: Array<PieChartData>;
  public brandAffinityChartData: Array<{name: string, value: number}>;
  public genderPerAgeChart: Array<GenderPerAgeChartData>;
  public genderPerAgeChartMaxPercent: number;
  public influencerId: number;
  public errorMessage: string;
  public isVisibleChart: boolean | false;

  public countryList: Array<object>;
  public languageList: Array<object>;

  @Input() deepSocialData: any;
  @Input() demoInfluencerId: number;
  @Input() pieCenter: number[];
  @Input() pieSize: number;
  @Input() pieInnerSize: number;
  @Input() pieLegend: any;
  @Input() pieHeight: number;
  @Input() pieTitleStyling: any;


  @Output() afterViewInit = new EventEmitter<any>();

  // use for segement track event
  public influencerName: string;
  // items placeholder for brand
  public placeholderItems = new Array(15);

  /**
   * get max percent number of GenderPerAge data
   * @param data
   */
  static getMaxPercentOfGenderPerAge(data: Array<GenderPerAgeChartData>) {
    const femaleArray = data.map(v => (v.f));
    const maleArray = data.map(v => (v.m));
    const array = femaleArray.concat(maleArray);
    const maxValue = Math.max(...array);
    return maxValue;
  }

  constructor(
    private userService: UserService,
    private referenceDataService: ReferenceDataService,
    private translate: TranslateService,
    private pdfService: PdfExportService,
  ) {}

  private mapData(data: object) {
    const result = Object.keys(data).map((key) => ({name: key, y: data[key]}));
    return result;
  }

  /**
   * process raw data to get top 20 brand affinity without duplicated items
   * @param data
   */
  private processBrandAffinityData(data: object) {
    // Transform data into {name, value} format
    const formatArray = Object.keys(data).map(
      (key) => Object.keys(data[key]).map((sub_key) => ({
        name: sub_key,
        value: data[key][sub_key]
      }))
    );

    // Flatten an array of arrays
    const flatArray = formatArray.reduce((array, value) => array.concat(value), []);

    // Filter duplicated items by name
    const filteredArray = flatArray
    .filter(
      (value, index, array) =>
        array.findIndex((newValue) => newValue.name === value.name) === index) // Remove duplicates from js array (ES5/ES6)  https://gist.github.com/telekosmos/3b62a31a5c43f40849bb
    // Sort by value to get top from the highest value
    .sort((a, b) => b.value - a.value)
    // Get top 20 item
    .slice(0, CommonConstant.BRAND_AFFINITY_TOP_ITEM);

    return filteredArray;
  }

  /**
   * calculate Top 10 Items for pie chart
   * @param data
   */
  private calculateTopItem(data: Array<PieChartData>) {
    // Get max 10 items
    const selectedItems = data.slice(0, CommonConstant.PIE_CHART_TOP_ITEM);

    // Get the sum of percentage for selected items
    const totalPercent = selectedItems.reduce(
      (accumulator, value) => accumulator + value.y,
      0
    );

    // Recalculate item percentages to be proportional to the sum of the top 10 items
    const calculatedItems = selectedItems.map(
      (item) => Object.assign(
        item,
        { y: (item.y * (100 / (totalPercent * 100)))}
      )
    );

    return calculatedItems;
  }

  /**
   * fill constanst name (language, country,...) by using code
   * @param data, constants
   */
  private fillCodeConstanst(data: Array<any>, constants: Array<any>) {
    // create hash map constanst
    const constantsHash = constants.reduce((map, obj) => (map[obj.code] = obj.name, map), {});

    // Assign data point code to get name from contanst hash
    const transformedData = data.map(
      (item) => Object.assign(item, {
        name: constantsHash[item.name]
      })
    );

    return transformedData;
  }

  private mapKeyInObjectIntoArray(data: object) {
    const result = Object.keys(data).map((key) => key);
    return result;
  }

  private parseJSON(data: string) {
    let result = null;
    try {
      result = JSON.parse(data);
    } catch (error) {
      Sentry.captureException(error);
    }
    return result;
  }

  private transformGenderPerAgeChartData(data: object) {
    const result = Object.keys(data).map(
      (key) => ({
        range: key,
        m: Math.round(data[key].MALE * 100),
        f: Math.round(data[key].FEMALE * 100)
      })
    ).filter(line => line.f > 0 || line.m > 0);

    return result;
  }

  private getDemographicsData() {
    // no deep social data. Stop immediately
    if (!this.deepSocialData) {
      this.afterViewInit.emit(true);
      return;
    }

    this.rawChartData = {
      content_persons_statistic: this.parseJSON(this.deepSocialData.content_persons_statistic),
      content_geo_statistic: this.parseJSON(this.deepSocialData.content_geo_statistic),
      audience_interests: this.parseJSON(this.deepSocialData.audience_interests),
      audience_languages: this.parseJSON(this.deepSocialData.audience_languages),
      brand_affinity: this.parseJSON(this.deepSocialData.brand_affinity),
      audience_brand_affinity: this.parseJSON(this.deepSocialData.audience_brand_affinity),
    };

    if (this.rawChartData.content_persons_statistic) {
      this.genderChartData = this.mapData(this.rawChartData.content_persons_statistic.genders);
      for (let i = 0; i < this.genderChartData.length; i++) {
        if (this.genderChartData[i] && this.genderChartData[i].name === InfluencerDemographicGender.Male.toUpperCase()) {
          this.genderChartData[i].name = InfluencerDemographicGender.Male;
        }
        if (this.genderChartData[i] && this.genderChartData[i].name === InfluencerDemographicGender.Female.toUpperCase()) {
          this.genderChartData[i].name = InfluencerDemographicGender.Female;
        }
      }

      this.genderPerAgeChart = this.transformGenderPerAgeChartData(this.rawChartData.content_persons_statistic.gendersPerAge);
      this.genderPerAgeChartMaxPercent = InfluencerDemographicsComponent.getMaxPercentOfGenderPerAge(this.genderPerAgeChart);
    }

    if (this.rawChartData.content_geo_statistic) {
      this.countryChartData = this.calculateTopItem(this.mapData(this.rawChartData.content_geo_statistic.countries));
      this.countryChartData = this.fillCodeConstanst(this.countryChartData, this.countryList);
    }

    if (this.rawChartData.audience_interests) {
      this.audienceInterestChartData = this.calculateTopItem(this.mapData(this.rawChartData.audience_interests));
    }
    if (this.rawChartData.audience_languages) {
      this.nativeLanguageChartData = this.calculateTopItem(this.mapData(this.rawChartData.audience_languages));
      this.nativeLanguageChartData = this.fillCodeConstanst(this.nativeLanguageChartData, this.languageList);
    }
    if (this.rawChartData.audience_brand_affinity) {
      this.brandAffinityChartData = this.processBrandAffinityData(this.rawChartData.audience_brand_affinity);
    }

    this.isVisibleChart = true;
    this.afterViewInit.emit(true);
  }

  private loadDefinitionData() {
    this.countryList = this.referenceDataService.countries;
    this.languageList = this.referenceDataService.languages;
    this.getDemographicsData();
  }

  get isFreeUser() {
    return this.userService.isFreeUser();
  }

  get isAdmin() {
    return this.userService.isAdmin;
  }

  get fallbackTitle() {
    if (this.userService.isFreeUser()) {
      return this.translate.instant('common.invalid_action_by_levels_warning.fallback_title');
    } else {
      return null;
    }
  }

  get fallbackMessage() {
    if (this.userService.isFreeUser()) {
      return this.translate.instant('common.invalid_action_by_levels_warning.fallback_message');
    } else {
      return null;
    }
  }

  ngOnInit() {
    this.loadDefinitionData();
  }

  ngAfterViewInit() {
    this.pdfService.incrementNumberReportReady(InfluencerDemographicsComponent.COMPONENT_NAME);
  }
}



// WEBPACK FOOTER //
// ./src/app/main/influencer/influencer-demographics/influencer-demographics.component.ts